import type { ImageUpload } from '@adplay/common'
import { type QueryDocumentSnapshot, type SnapshotOptions, Timestamp, collection, deleteDoc, getDoc } from 'firebase/firestore'
import {
  doc,
  getFirestore,
  setDoc,
} from 'firebase/firestore'
import type { MarkRequired } from 'ts-essentials'

export interface BrandLogo {
  theme: 'transparent' | 'dark' | 'light'
  type: 'logo' | 'icon'
  upload: ImageUpload
}
export type BrandParams = MarkRequired<Partial<Brand>, 'teamId'>
export class Brand {
  id?: string
  teamId: string
  name: string
  description: string
  logos: ImageUpload[]
  colors: string[]

  createdAt: Date
  updatedAt: Date

  static toFirestore(brand: Brand) {
    return {
      id: brand.id,
      teamId: brand.teamId,
      name: brand.name,
      description: brand.description,
      logos: brand.logos,
      colors: brand.colors,
      createdAt: Timestamp.fromDate(brand.createdAt),
      updatedAt: Timestamp.fromDate(brand.updatedAt),
    }
  }

  static fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): Brand {
    const data = snapshot.data()

    return new Brand({
      id: snapshot.id,
      teamId: data.teamId,
      name: data.name,
      description: data.description,
      logos: data.logos,
      colors: data.colors,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    })
  }

  constructor(options: BrandParams) {
    this.id = options.id
    this.teamId = options.teamId

    this.name = options.name || ''
    this.description = options.description || ''
    this.logos = options.logos || []
    this.colors = options.colors?.map(color => toRgbString(color) as string) || []
    this.createdAt = options.createdAt || new Date()
    this.updatedAt = options.updatedAt || new Date()
  }

  save() {
    return setDoc(this.getFirestoreDoc(), this, { merge: true })
  }

  delete() {
    return deleteDoc(this.getFirestoreDoc())
  }

  getFirestoreDoc() {
    const db = getFirestore()

    if (!this.id) {
      const brandDoc = doc(collection(db, `/teams/${this.teamId}/brands`)).withConverter(Brand)

      this.id = brandDoc.id

      return brandDoc
    }

    return doc(db, `/teams/${this.teamId}/brands/${this.id}`).withConverter(Brand)
  }

  get primaryLogoUrl() {
    if (!this.logos.length)
      return `/images/ad-placeholder-image.svg`

    return this.logos[0].url
  }

  toJSON(): BrandParams {
    return {
      id: this.id,
      teamId: this.teamId,
      name: this.name,
      description: this.description,
      logos: this.logos,
      colors: this.colors,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    }
  }

  async isDefaultBrand() {
    const db = getFirestore()

    const teamDoc = doc(db, `/teams/${this.teamId}`)

    const teamSnapshot = await getDoc(teamDoc)

    if (!teamSnapshot.exists())
      return false

    const team = teamSnapshot.data()

    return team?.defaultBrandId === this.id
  }

  async setAsDefaultBrand() {
    const db = getFirestore()

    return setDoc(doc(db, `/teams/${this.teamId}`), { defaultBrandId: this.id }, { merge: true })
  }
}
